import cs from 'classnames';
import moment from 'moment-timezone';
import React, { CSSProperties, useState } from 'react';
import {
  Badge, Dropdown, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import { showToast } from '../../ui/utils';
import {
  inactiveReason, scheduleStatus, ScheduleStatus, WorkingItem,
} from './discoverShelvesApi';
import styles from './ItemCard.module.scss';

// Cards keep the app's 438x600 portrait cover ratio.
export const CARD_WIDTH = 110;

// The selection border is drawn inside the card (an outline would be clipped by the scrolling strip),
// so every card reserves it and only its colour changes.
const CARD_BORDER = 3;
const CARD_PADDING = 2;

export const CARD_OUTER_WIDTH = CARD_WIDTH + 2 * (CARD_BORDER + CARD_PADDING);

const COVER_HEIGHT = Math.round((CARD_WIDTH * 600) / 438);
const DATE_FORMAT = 'MMM D YYYY h:mm A';
const TOOLTIP_DELAY_MS = 500;

const STATUS_BADGES: Record<ScheduleStatus, { label: string; variant: string } | null> = {
  live: null,
  scheduled: { label: 'Scheduled', variant: 'info' },
  expired: { label: 'Expired', variant: 'secondary' },
  inactive: { label: 'Inactive', variant: 'warning' },
};

export function formatSchedule(startTime: string | null, endTime: string | null, timeZone: string) {
  if (!startTime || !endTime) {
    return 'Always';
  }
  const format = 'MMM D, h:mm A';
  return `${moment(startTime).tz(timeZone).format(format)} – ${moment(endTime).tz(timeZone).format(format)}`;
}

const copyToClipboard = (text: string) => navigator.clipboard.writeText(text);

// Shows the full text in a tooltip after a short hover; clicking while the tooltip is showing copies it.
function CopyableText(props: {
  text: string;
  children: React.ReactNode;
  className?: string;
  style?: CSSProperties;
  wide?: boolean; // keep each line of the tooltip on one line
}) {
  const {
    text, children, className, style, wide,
  } = props;
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);

  return (
    <OverlayTrigger
      placement="top"
      delay={{ show: TOOLTIP_DELAY_MS, hide: 100 }}
      show={show}
      onToggle={(next) => {
        setShow(next);
        if (!next) {
          setCopied(false);
        }
      }}
      overlay={(
        <Tooltip id={`copy-${text}`} className={wide ? styles.wideTooltip : undefined} style={{ whiteSpace: 'pre-line' }}>
          {copied ? 'Copied!' : text}
          {!copied && <div className="small">(click to copy)</div>}
        </Tooltip>
      )}
    >
      <div
        className={className}
        style={{ ...style, cursor: show ? 'copy' : undefined }}
        onClick={() => {
          if (show) {
            copyToClipboard(text).then(() => setCopied(true)).catch(() => undefined);
          }
        }}
        onKeyDown={undefined}
        role="presentation"
      >
        {children}
      </div>
    </OverlayTrigger>
  );
}

export interface ItemCardAction {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export const itemTitle = (item: WorkingItem) => item.book?.title || 'Unknown book';

export const copyActions = (item: WorkingItem): ItemCardAction[] => [
  {
    label: 'Copy title',
    onClick: () => copyToClipboard(itemTitle(item))
      .then(() => showToast({ textMessage: 'Title copied', variant: 'success', timeout: 1500 })),
  },
  {
    label: 'Copy UUID',
    onClick: () => copyToClipboard(item.book_uuid)
      .then(() => showToast({ textMessage: 'UUID copied', variant: 'success', timeout: 1500 })),
  },
];

interface ItemCardProps {
  item: WorkingItem;
  timeZone: string;
  isDuplicate?: boolean;
  hiddenByDuplicate?: boolean;
  highlighted?: boolean;
  actions?: ItemCardAction[];
  // Clear Books mode: the menu is replaced by a toggle, and clicking the card toggles it.
  selection?: { selected: boolean; onToggle: () => void };
  hideMenu?: boolean;
}

export function ItemCard(props: ItemCardProps) {
  const {
    item, timeZone, isDuplicate, hiddenByDuplicate, highlighted, actions = [], selection, hideMenu,
  } = props;
  const status = scheduleStatus(item);
  const badge = STATUS_BADGES[status];
  const dimmed = status === 'expired' || status === 'inactive';
  const coverUrl = item.cover_url || item.book?.coverUrl;
  const title = itemTitle(item);
  const menuActions = actions.length ? actions : copyActions(item);
  const selected = Boolean(selection?.selected);

  const coverStyle: CSSProperties = {
    width: CARD_WIDTH,
    height: COVER_HEIGHT,
    borderRadius: 4,
    background: coverUrl ? `center / cover no-repeat url("${coverUrl}")` : '#ddd',
    opacity: dimmed ? 0.4 : 1,
  };

  return (
    <div
      style={{
        width: CARD_OUTER_WIDTH,
        fontSize: '0.75rem',
        padding: CARD_PADDING,
        borderRadius: 6,
        border: `${CARD_BORDER}px solid ${(selected && '#dc3545') || (highlighted && '#007bff') || 'transparent'}`,
        background: selected ? 'rgba(220, 53, 69, 0.12)' : 'white',
        cursor: selection ? 'pointer' : undefined,
      }}
      onClick={selection ? selection.onToggle : undefined}
      onKeyDown={undefined}
      role="presentation"
    >
      <div style={{ position: 'relative' }}>
        <div style={coverStyle} />
        {selection ? (
          // Drawn, not a form control: a Bootstrap switch's label and hidden input each send a click to
          // the card, toggling twice. This way the card's own click toggles exactly once.
          <div
            className={cs(styles.toggle, selected && styles.toggleOn)}
            style={{ position: 'absolute', top: 4, right: 4 }}
            role="switch"
            aria-checked={selected}
            aria-label="Select to clear"
          >
            <div className={styles.toggleKnob} />
          </div>
        ) : !hideMenu && (
          // Stop pointer events here so opening the menu never starts a drag.
          <div
            style={{ position: 'absolute', top: 2, right: 2 }}
            onPointerDown={(event) => event.stopPropagation()}
          >
            <Dropdown>
              <Dropdown.Toggle size="sm" variant="light" style={{ padding: '0 4px' }}>⋯</Dropdown.Toggle>
              <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                {menuActions.map((action) => (
                  <Dropdown.Item key={action.label} onClick={action.onClick} disabled={action.disabled}>
                    {action.label}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </div>
        )}
      </div>
      <CopyableText
        text={title}
        className="font-weight-bold mt-1"
        style={{
          display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', wordBreak: 'break-word',
        }}
      >
        {title}
      </CopyableText>
      <CopyableText text={item.book_uuid} className="text-muted text-truncate">{item.book_uuid.slice(0, 8)}</CopyableText>
      {item.start_time && item.end_time && (
        <CopyableText
          text={`Start: ${moment.utc(item.start_time).format(DATE_FORMAT)} UTC\nEnd: ${moment.utc(item.end_time).format(DATE_FORMAT)} UTC`}
          style={{ whiteSpace: 'nowrap', fontSize: '0.6rem' }}
          wide
        >
          <div>{moment(item.start_time).tz(timeZone).format(DATE_FORMAT)}</div>
          <div>{moment(item.end_time).tz(timeZone).format(DATE_FORMAT)}</div>
        </CopyableText>
      )}
      <div className="d-flex flex-wrap" style={{ gap: 2 }}>
        {badge && (
          <Badge variant={badge.variant} title={status === 'inactive' ? inactiveReason(item) : undefined}>
            {badge.label}
          </Badge>
        )}
        {item.book && !item.book.published && <Badge variant="warning">Unpublished</Badge>}
        {item.cover_key && <Badge variant="primary">Custom cover</Badge>}
        {isDuplicate && (
          <Badge
            variant={hiddenByDuplicate ? 'danger' : 'dark'}
            title={hiddenByDuplicate
              ? 'Hidden at the preview time: another copy of this book on this shelf wins'
              : 'This book is on this shelf more than once'}
          >
            {hiddenByDuplicate ? 'Duplicate (hidden)' : 'Duplicate'}
          </Badge>
        )}
        {item.promotion && (
          <Badge
            variant="success"
            title={`${item.promotion.title} (${item.promotion.identifier}) – ${item.promotion.status}`}
          >
            {item.promotion.identifier}
          </Badge>
        )}
      </div>
    </div>
  );
}
